/**
 * Hook: GET /api/t/{tenantSlug}/interview-analysis/by-resource — Batch 30j-3.
 *
 * URL note (corrected in Batch 30j-4): the AI router lives at
 * ``/api/t/{tenant_slug}`` via ``tenant_router`` (`backend/app/__init__.py:381`
 * + `backend/app/api/tenant.py:14`). The original 30j-3 hooks used the bare
 * ``/api/pipeline/...`` path which 404s on cold-cache fetches in production
 * (``apiClient.baseURL`` is empty — no rewrite). Tests passed because they
 * mock URLs. URL aligned with the tenant-router canon in Batch 30j-4.
 *
 * Returns the latest `interview_analyses` row for the given candidate +
 * type, gated on tab activation (`enabled`). Polls every 3s while
 * `status ∈ {queued, running}`; stops on `complete` / `failed`.
 *
 * Design references:
 *   - `frontend/docs/30j_3_design.md` §3 — state machine + polling cadence
 *   - `frontend/docs/30j_3_architect.md` §4 — hook signature + LCP gate
 *   - `frontend/docs/30j_3_security.md` §5 (HARD-4) — polling-pause invariant,
 *     §6 (HARD-5) — query-key shape isolates per-candidate cache,
 *     §12 (HARD-11) — 404 → null (empty), not error.
 *
 * Hard rules:
 *   - 404 from `by-resource` is the LEGITIMATE empty state. Hook catches
 *     it at the queryFn boundary and returns `null`. The phase derivation
 *     downstream maps `null` → `"empty"`, NOT `"error"`.
 *   - 404s do NOT retry. Other errors retry up to 2 times.
 *   - Query key shape: `["interview-analysis", "by-resource", tenantSlug,
 *     candidateId, type, jdId]`. Two candidates have two keys; cross-
 *     candidate cache leakage is structurally impossible.
 *   - `enabled` gates on `activeTab === "eval"` AND a candidateId/jdId/
 *     tenantSlug being set. When `enabled === false`, queryFn is NEVER
 *     invoked — this is the LCP guarantee for render-and-hide.
 *   - The wire endpoint takes `candidate_id`, `type`, and `jd_id`.
 *     `jd_id` is sent on the wire so the server can auto-enqueue
 *     generation for top-N candidates when the eval row is missing
 *     (lazy backfill). The returned row is still the latest pre-analysis
 *     by `(candidate, type)` — the read path is unchanged; `jd_id` is
 *     used server-side only for backfill scoping.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import { useInvalidateJournalOnComplete } from "@/hooks/queries/journal/useInvalidateJournalOnComplete";

export interface InterviewAnalysisRow {
  id: string;
  type: "pre" | "post";
  candidate_id: string;
  jd_id: string | null;
  /**
   * Per-round dimension (backend migration 0124). The stable id of a round
   * inside `jd.interview_rounds`. `null` for legacy / "unassigned" analyses
   * and for the untouched 'pre' path. Optional — absent on rows serialized
   * before the field existed (backfill-tolerant, like `conversation_health`).
   */
  round_id?: string | null;
  status: "queued" | "running" | "complete" | "failed";
  analysis: string | null;
  model_used: string | null;
  transcript: string | null;
  error: string | null;
  started_by: string;
  created_at: string;
  completed_at: string | null;
}

interface UseInterviewAnalysisOpts {
  candidateId: string | undefined;
  type: "pre" | "post";
  jdId: string | undefined;
  /**
   * Tab-activation gate. When false, the queryFn is NEVER invoked
   * (no fetch, no poll). Pass `activeTab === "eval"` from the consumer.
   */
  enabled: boolean;
  /**
   * Per-round read (backend migration 0124). When `roundScoped` is true the
   * result is constrained to a single round: a concrete `roundId` matches that
   * round; `roundId=null` matches the legacy / "unassigned" (`round_id IS
   * NULL`) slot. Omit / leave false to preserve the pre-0124 latest-wins read
   * (the untouched 'pre' path passes neither).
   */
  roundId?: string | null;
  roundScoped?: boolean;
}

export function useInterviewAnalysis(
  opts: UseInterviewAnalysisOpts,
): UseQueryResult<InterviewAnalysisRow | null, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { candidateId, type, jdId, enabled, roundScoped = false } = opts;
  const roundId = opts.roundId ?? null;

  const query = useQuery<InterviewAnalysisRow | null, ApiError>({
    // Cache key isolates per-(tenant, candidate, type, jd, round). HARD-5.
    queryKey: [
      "interview-analysis",
      "by-resource",
      tenantSlug,
      candidateId,
      type,
      jdId,
      roundScoped ? roundId : "unscoped",
    ],
    queryFn: async () => {
      try {
        const r = await apiClient.get<InterviewAnalysisRow>(
          `/api/t/${tenantSlug}/interview-analysis/by-resource`,
          {
            params: {
              candidate_id: candidateId,
              type,
              jd_id: jdId,
              ...(roundScoped
                ? { round_scoped: true, ...(roundId ? { round_id: roundId } : {}) }
                : {}),
            },
          },
        );
        return r.data;
      } catch (err) {
        // 404 → empty row, not error. Same pattern as useRankingRunByResource.
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: enabled && !!tenantSlug && !!candidateId && !!jdId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === "queued" || data.status === "running")) {
        return 3000;
      }
      return false;
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    // Cached row stays "fresh" for 30s so a tab cycle does NOT trigger
    // an immediate refetch (which would visibly flicker between
    // cached state and refetch landing).
    staleTime: 30_000,
  });

  // Card 2 §7 — surface a freshly-completed evaluation/analysis in the
  // Actions Log without requiring navigation.
  useInvalidateJournalOnComplete(query.data?.status);

  return query;
}
